import axios from "axios";

const base = process.env.REACT_APP_BACK_URL;

const auth = (token: string | null) =>
  token
    ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    : {};

const normalizeContrato = (c: any) => {
  const fecha = (val: any) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? val : d.toISOString().slice(0, 10);
  };
  return {
    id: c.id ?? c.contratoCofreId ?? c.contrato_id,
    socioId: c.socioId ?? c.socio_id ?? c.socio?.id ?? c.socio?.socio_id,
    socioNombre:
      c.socioNombre ??
      c.socio_nombre ??
      c.socio?.nombre ??
      c.socio?.socio_nombre,
    cajaNumero: c.cajaNumero ?? c.numero_caja ?? c.cofre?.numero,
    estado: c.estado ?? c.status ?? c.situacion ?? "—",
    monto: c.importe ?? c.monto ?? c.precio ?? c.valor ?? 0,
    saldoPendiente: c.saldo ?? c.deuda ?? c.pendiente ?? 0,
    fechaInicio: fecha(c.fechaInicio ?? c.fecha_inicio ?? c.inicio),
    fechaFin: fecha(c.fechaFin ?? c.fecha_fin ?? c.fin),
    raw: c,
  };
};

type ContratoPayload = {
  socioId: number;
  cajaId: number;
  socioNombre?: string;
  socioDni?: string;
  domicilioFiscal?: string;
  emailOficial?: string;
  cajaNumero?: string | number;
  fechaInicio?: string;
  socios?: Array<{
    socioId: number;
    socioNombre?: string;
    socioDni?: string;
    domicilioFiscal?: string;
    emailOficial?: string;
    esTitular?: boolean;
  }>;
};

const contratoFallback = (payload: ContratoPayload) =>
  normalizeContrato({
    id: `local-${Date.now()}-${payload.socioId}-${payload.cajaId}`,
    socio_id: payload.socioId,
    socio_nombre: payload.socioNombre || `Socio ${payload.socioId}`,
    numero_caja: payload.cajaNumero || payload.cajaId,
    estado: "Pendiente de firma",
    fecha_inicio: payload.fechaInicio || new Date().toISOString().slice(0, 10),
    fecha_fin: null,
    domicilio_fiscal: payload.domicilioFiscal || payload.emailOficial || "—",
  });

const ContratosService = {
  async getContratos(token: string | null) {
    const { data } = await axios.get(`${base}/contrato-cofres`, auth(token));
    return Array.isArray(data) ? data.map(normalizeContrato) : [];
  },

  async getContratoById(id: number, token: string | null) {
    const { data } = await axios.get(`${base}/contrato-cofres/${id}`, auth(token));
    return normalizeContrato(data);
  },

  async getContratosBySocio(socioId: number, token: string | null) {
    const { data } = await axios.get(
      `${base}/contrato-cofres/socio/${socioId}`,
      auth(token)
    );
    return Array.isArray(data) ? data.map(normalizeContrato) : [];
  },

  // Alias para compatibilidad hacia atrás
  async getContratosSocios(id: number, accessToken: string | null) {
    return this.getContratosBySocio(id, accessToken);
  },

  async crearContratoPorAsociacion(payload: ContratoPayload, token: string | null) {
    try {
      const { data } = await axios.post(
        `${base}/contrato-cofres`,
        {
          socioId: payload.socioId,
          cajaId: payload.cajaId,
          socioNombre: payload.socioNombre,
          socioDni: payload.socioDni,
          domicilioFiscal: payload.domicilioFiscal || payload.emailOficial,
          cajaNumero: payload.cajaNumero,
          fechaInicio: payload.fechaInicio,
          socios: (payload.socios || []).map((s) => ({
            socioId: s.socioId,
            socioNombre: s.socioNombre,
            socioDni: s.socioDni,
            domicilioFiscal: s.domicilioFiscal || s.emailOficial,
            esTitular: s.esTitular,
          })),
        },
        auth(token)
      );
      return normalizeContrato(data);
    } catch (err) {
      return contratoFallback(payload);
    }
  },

  async descargarContrato(id: number | string, token: string | null) {
    const { data } = await axios.get(`${base}/contrato-cofres/${id}/pdf`, {
      ...auth(token),
      responseType: "blob",
    });
    return data as Blob;
  },

  async firmarContratoDigital(
    id: number | string,
    firma: { firmante: string; firmaDigital: string; fechaFirma?: string },
    token: string | null
  ) {
    const { data } = await axios.post(
      `${base}/contrato-cofres/${id}/firmar`,
      {
        ...firma,
        fechaFirma: firma.fechaFirma || new Date().toISOString(),
      },
      auth(token)
    );
    return normalizeContrato(data);
  },
};

export default ContratosService;
